type LogoSize = 'sm' | 'md' | 'lg' | 'xl';
type LogoTone = 'default' | 'inverted';

interface LogoProps {
  size?: LogoSize;
  /** `default`: ícone colorido + wordmark em --color-foreground. `inverted`: tudo em branco para fundos escuros/coloridos. */
  tone?: LogoTone;
  /** Renderiza apenas o ícone, sem o wordmark "NTConsult". */
  iconOnly?: boolean;
  className?: string;
}

const ICON_PX: Record<LogoSize, number> = { sm: 20, md: 28, lg: 40, xl: 72 };
const WORDMARK_CLASS: Record<LogoSize, string> = {
  sm: 'text-sm',
  md: 'text-lg',
  lg: 'text-2xl',
  xl: 'text-4xl',
};

/**
 * Lockup da marca NTConsult: ícone SVG inline + wordmark na fonte Aptos do DS.
 * Cores oficiais (azul/verde) são preservadas no modo `default`. Em `inverted`
 * o lockup inteiro vira branco — usar sobre fundos com a cor da brand.
 */
export function Logo({ size = 'md', tone = 'default', iconOnly = false, className }: LogoProps) {
  const px = ICON_PX[size];
  const inverted = tone === 'inverted';

  const wrapperRole = iconOnly ? { role: 'img' as const, 'aria-label': 'NTConsult' } : {};

  return (
    <span
      className={`inline-flex items-center gap-2 leading-none ${className ?? ''}`}
      {...wrapperRole}
    >
      <svg
        width={px}
        height={px}
        viewBox="0 0 48 49"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <title>NTConsult</title>
        <path
          d="M25.096 2.75934V25.3879H2.71308V48.0165H47.4789V2.75934H25.096Z"
          fill={inverted ? 'currentColor' : '#84D340'}
        />
        <path
          d="M22.3829 0.0164795H0V22.6451H22.3829V0.0164795Z"
          fill={inverted ? 'currentColor' : '#3980FF'}
          fillOpacity={inverted ? 0.85 : 1}
        />
      </svg>
      {!iconOnly && (
        <span className={`font-semibold tracking-tight ${WORDMARK_CLASS[size]}`}>
          <span className={inverted ? '' : 'text-foreground'}>NT</span>
          <span className={inverted ? 'opacity-90' : 'text-foreground-muted'}>Consult</span>
        </span>
      )}
    </span>
  );
}
